<template>
  <div class="question-container">
    <!-- 顶部导航 -->
    <TopHeader :fixed="true" />
    
    <!-- 工具栏 -->
    <div class="toolbar-fixed">
      <div class="toolbar">
        <div class="back-link" @click="showExitModal = true">
          <img src="@/assets/image/back.png" class="back-icon" />
          <span>退出考试</span>
        </div>
        <div class="title">{{ examTitle }}</div>
        <div class="toolbar-right">
          <button class="submit-btn" @click="showSubmitModal = true">交卷</button>
          <div class="timer" :class="{ warning: timer < 300 }">
            <img src="@/assets/image/time.png" class="timer-icon" />
            <span>{{ formatTime }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-wrapper">
      <div class="content-card">
        <!-- 题型介绍 -->
        <div v-if="questionIntro" class="question-intro-section">
          <div class="intro-title">题型介绍</div>
          <div class="intro-content">{{ questionIntro }}</div>
        </div>

        <!-- 加载中 -->
        <div v-if="loading" class="loading-box">加载中...</div>

        <!-- 题目列表 -->
        <div v-else class="question-list">
          <div 
            v-for="(q, index) in questions" 
            :key="index" 
            :id="'question-' + index"
            class="question-item"
          >
            <div class="question-header">
              <span class="question-title">{{ index + 1 }}.{{ q.title }}</span>
              <div class="question-right">
                <span :class="['question-type', q.type === '1' ? 'single' : 'multi']">
                  {{ q.type === '1' ? '单选题' : '多选题' }}
                </span>
                <span class="question-score">（{{ q.score }}分）</span>
              </div>
            </div>
            <div class="options">
              <div 
                v-for="(opt, optIndex) in q.options" 
                :key="optIndex"
                :class="['option', { selected: q.selected.includes(optIndex) }]"
                @click="selectOption(index, optIndex)"
              >
                <span :class="['option-label', q.type === '1' ? 'circle' : 'square']">{{ opt.label }}</span>
                <span v-if="opt.content" class="option-text">{{ opt.content }}</span>
                <img v-else-if="opt.image" :src="baseURL + opt.image" class="option-image" @click.stop="showPreview(baseURL + opt.image)" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 回到顶部 -->
      <div class="back-top" @click="scrollToTop">
        <img src="@/assets/image/up.png" class="arrow-icon" />
        <span>顶部</span>
      </div>
    </div>

    <!-- 底部答题卡 -->
    <div :class="['footer-bar', { expanded: showAnswerCard }]">
      <div class="footer-content">
        <div class="answer-card-toggle" @click="showAnswerCard = !showAnswerCard">
          <img :src="showAnswerCard ? require('@/assets/image/down.png') : require('@/assets/image/up.png')" class="arrow-icon" />
          <span>答题卡</span>
        </div>
        <div v-if="showAnswerCard" class="answer-stats">
          <span class="stat-item answered">
            <span class="stat-dot"></span>
            已答（{{ answeredCount }}）
          </span>
          <span class="stat-item unanswered">
            <span class="stat-dot"></span>
            未答（{{ unansweredCount }}）
          </span>
        </div>
        <div v-else class="pagination-row">
          <span 
            v-for="(q, index) in questions" 
            :key="index"
            :class="['page-num', { answered: q.selected.length > 0 }]"
            @click="scrollToQuestion(index)"
          >{{ index + 1 }}</span>
        </div>
      </div>
      <div v-if="showAnswerCard" class="answer-card-grid">
        <span 
          v-for="(q, index) in questions" 
          :key="index"
          :class="['grid-num', { answered: q.selected.length > 0 }]"
          @click="scrollToQuestion(index)"
        >{{ index + 1 }}</span>
      </div>
    </div>

    <!-- 交卷弹窗 -->
    <div v-if="showSubmitModal" class="modal-overlay" @click.self="showSubmitModal = false">
      <div class="modal-box">
        <div class="modal-content">
          <template v-if="unfinishedCount > 0">
            您有{{ unfinishedCount }}道题未做，确定交卷吗？
          </template>
          <template v-else>
            确定提交试卷吗？
          </template>
        </div>
        <div class="modal-buttons">
          <button class="modal-btn confirm" @click="confirmSubmit">确定</button>
          <button class="modal-btn cancel" @click="showSubmitModal = false">取消</button>
        </div>
      </div>
    </div>

    <!-- 退出考试弹窗 -->
    <div v-if="showExitModal" class="modal-overlay" @click.self="showExitModal = false">
      <div class="modal-box">
        <div class="modal-content">
          确定退出考试吗？退出后可重新进入继续作答。
        </div>
        <div class="modal-buttons">
          <button class="modal-btn confirm" @click="confirmExit">确定</button>
          <button class="modal-btn cancel" @click="showExitModal = false">取消</button>
        </div>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <div v-if="previewImage" class="image-preview-overlay" @click="previewImage = ''">
      <img :src="previewImage" class="preview-img" @click.stop />
    </div>
  </div>
</template>

<script>
import TopHeader from '@/components/TopHeader.vue'
import { startExam, getExamQuestions, submitExam } from '@/api/exam'
import { getSystemConfig } from '@/api/systemConfig'
import { decodeId, encodeId } from '@/utils/idEncoder'
import config from '@/utils/config'

export default {
  name: 'ExamPage',
  components: {
    TopHeader
  },
  data() {
    return {
      baseURL: config.imageBaseURL,
      examId: null,
      recordId: null,
      examTitle: '模拟考试',
      timer: 0,
      timerInterval: null,
      showSubmitModal: false,
      showExitModal: false,
      showAnswerCard: false,
      loading: true,
      questions: [],
      previewImage: '',
      questionIntro: ''
    }
  },
  computed: {
    formatTime() {
      const h = Math.floor(this.timer / 3600)
      const m = Math.floor((this.timer % 3600) / 60)
      const s = this.timer % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },
    answeredCount() {
      return this.questions.filter(q => q.selected.length > 0).length
    },
    unansweredCount() {
      return this.questions.filter(q => q.selected.length === 0).length
    },
    unfinishedCount() {
      return this.questions.filter(q => q.selected.length === 0).length
    },
    usedTime() {
      // 计算已用时间（秒）
      return this.questions.length > 0 ? (this.questions[0].duration * 60 - this.timer) : 0
    }
  },
  created() {
    this.examId = decodeId(this.$route.params.examId)
    if (this.examId) {
      this.loadSystemConfig()
      this.initExam()
    } else {
      this.$router.push('/home')
    }
  },
  beforeDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
    }
  },
  methods: {
    async loadSystemConfig() {
      try {
        const res = await getSystemConfig()
        if (res.data && res.data.questionIntro) {
          this.questionIntro = res.data.questionIntro
        }
      } catch (error) {
        console.error('获取系统配置失败', error)
      }
    },
    async initExam() {
      this.loading = true
      try {
        // 开始考试，获取记录ID
        const startRes = await startExam(this.examId)
        
        this.recordId = startRes.data.recordId
        this.examTitle = startRes.data.examTitle
        this.timer = startRes.data.remainingTime

        // 获取考试题目
        const questionsRes = await getExamQuestions(this.examId)

        const data = questionsRes.data
        this.questions = data.questions.map(q => ({
          questionId: q.questionId,
          title: q.title,
          type: q.type,
          score: q.score,
          options: q.options,
          selected: [],
          duration: data.duration
        }))

        // 开始倒计时
        this.startTimer()
      } catch (error) {
        console.error('初始化考试失败', error)
        // 如果是登录过期，request.js已经处理了跳转
        if (error.message !== '登录已过期') {
          alert(error.message || '初始化考试失败')
          this.$router.push('/home')
        }
      } finally {
        this.loading = false
      }
    },
    startTimer() {
      this.timerInterval = setInterval(() => {
        if (this.timer > 0) {
          this.timer--
        } else {
          // 时间到，自动交卷
          this.confirmSubmit()
        }
      }, 1000)
    },
    selectOption(qIndex, optIndex) {
      const q = this.questions[qIndex]
      
      if (q.type === '1') {
        q.selected = [optIndex]
      } else {
        const idx = q.selected.indexOf(optIndex)
        if (idx > -1) {
          q.selected.splice(idx, 1)
        } else {
          q.selected.push(optIndex)
        }
      }
    },
    async confirmSubmit() {
      this.showSubmitModal = false
      
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
      }

      // 构建答案数据
      const answers = this.questions.map(q => ({
        questionId: q.questionId,
        userAnswer: q.selected.map(i => q.options[i].label).join(',')
      }))

      // 计算已用时间
      const duration = this.questions.length > 0 ? this.questions[0].duration : 0
      const usedTime = duration * 60 - this.timer

      try {
        const res = await submitExam({
          recordId: this.recordId,
          usedTime: usedTime,
          answers: answers
        })

        if (res.code === 200) {
          // 跳转到考试记录详情页
          this.$router.push('/exam-record-detail/' + encodeId(this.recordId))
        } else {
          alert(res.msg || '提交失败')
        }
      } catch (error) {
        console.error('提交考试失败', error)
        alert('提交失败，请重试')
      }
    },
    confirmExit() {
      this.showExitModal = false
      this.$router.push('/home')
    },
    scrollToTop() {
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    },
    scrollToQuestion(index) {
      const el = document.getElementById('question-' + index)
      if (el) {
        const headerHeight = 120
        const top = el.offsetTop - headerHeight
        document.documentElement.scrollTop = top
        document.body.scrollTop = top
      }
    },
    showPreview(url) {
      this.previewImage = url
    }
  }
}
</script>

<style scoped>
.question-container {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 70px;
  padding-top: 120px;
}

.toolbar-fixed {
  position: fixed;
  top: 52px;
  left: 0;
  right: 0;
  z-index: 999;
  background: #fff;
  border-top: 1px solid #e8e8e8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.toolbar {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 30px;
  position: relative;
}

.main-wrapper {
  max-width: 900px;
  margin: 20px auto;
  padding: 0 20px;
}

.content-card {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  min-height: 300px;
}

/* 题型介绍样式 */
.question-intro-section {
  padding: 20px 25px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
}

.intro-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.intro-content {
  font-size: 14px;
  color: #666;
  line-height: 1.8;
  text-align: justify;
}

.loading-box {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #999;
  font-size: 14px;
}

.back-link {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #333;
  cursor: pointer;
  font-size: 14px;
}

.back-icon {
  width: 16px;
  height: 16px;
}

.back-link:hover {
  color: #3d8c40;
}

.title {
  font-size: 15px;
  color: #333;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.submit-btn {
  padding: 8px 24px;
  background: #3d8c40;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.submit-btn:hover {
  background: #2e7d32;
}

.timer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 15px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  font-size: 14px;
  color: #333;
}

.timer.warning {
  color: #e74c3c;
  border-color: #e74c3c;
}

.timer-icon {
  width: 16px;
  height: 16px;
}

.question-list {
  padding: 0 25px;
}

.question-item {
  padding: 25px 0;
  border-bottom: 1px solid #f0f0f0;
}

.question-item:last-child {
  border-bottom: none;
}

.question-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 15px;
  margin-bottom: 18px;
}

.question-title {
  flex: 1;
  font-size: 15px;
  color: #333;
  line-height: 1.6;
}

.question-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.question-type {
  font-size: 14px;
  font-weight: 500;
}

.question-type.single {
  color: #3d8c40;
}

.question-type.multi {
  color: #e74c3c;
}

.question-score {
  font-size: 13px;
  color: #999;
}

.options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: #fff;
  border: 1px solid #e8e8e8;
  min-height: 54px;
  box-sizing: border-box;
}

.option:hover {
  background: #f8f8f8;
}

.option.selected {
  background: #e8f5e9;
  border-color: #3d8c40;
}

.option.selected .option-label {
  background: #3d8c40;
  color: #fff;
  border-color: #3d8c40;
}

.option-label {
  width: 26px;
  height: 26px;
  border: 2px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #666;
  flex-shrink: 0;
}

.option-label.circle {
  border-radius: 50%;
}

.option-label.square {
  border-radius: 4px;
}

.option-text {
  font-size: 14px;
  color: #333;
}

.option-image {
  height: 40px;
  width: auto;
  object-fit: contain;
  border-radius: 4px;
  cursor: pointer;
}

.back-top {
  position: fixed;
  right: 30px;
  bottom: 100px;
  padding: 10px 15px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 4px;
}

.arrow-icon {
  width: 12px;
  height: 12px;
}

.footer-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #e8e8e8;
  transition: all 0.3s;
}

.footer-bar.expanded {
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
}

.footer-content {
  max-width: 900px;
  margin: 0 auto;
  padding: 15px 25px;
  display: flex;
  align-items: center;
  gap: 25px;
}

.answer-card-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #666;
  flex-shrink: 0;
}

.answer-stats {
  display: flex;
  align-items: center;
  gap: 30px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
}

.stat-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
}

.stat-item.answered .stat-dot {
  background: #3d8c40;
}

.stat-item.unanswered .stat-dot {
  background: #fff;
  border: 2px solid #ddd;
}

.answer-card-grid {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 25px 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  max-height: 200px;
  overflow-y: auto;
}

.grid-num {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  background: #f5f5f5;
  color: #666;
  flex-shrink: 0;
}

.grid-num:hover {
  background: #e8e8e8;
}

.grid-num.answered {
  background: #3d8c40;
  color: #fff;
}

.pagination-row {
  display: flex;
  gap: 10px;
  flex: 1;
  overflow: hidden;
  height: 30px;
  mask-image: linear-gradient(to right, black calc(100% - 40px), transparent 100%);
  -webkit-mask-image: linear-gradient(to right, black calc(100% - 40px), transparent 100%);
}

.page-num {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  cursor: pointer;
  background: #f5f5f5;
  color: #666;
  flex-shrink: 0;
}

.page-num:hover {
  background: #e8e8e8;
}

.page-num.answered {
  background: #3d8c40;
  color: #fff;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-box {
  background: #fff;
  border-radius: 12px;
  padding: 40px 50px;
  min-width: 400px;
  text-align: center;
}

.modal-content {
  font-size: 18px;
  color: #333;
  margin-bottom: 35px;
  line-height: 1.6;
}

.modal-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
}

.modal-btn {
  width: 140px;
  height: 45px;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
}

.modal-btn.confirm {
  background: #3d8c40;
  color: #fff;
  border: none;
}

.modal-btn.cancel {
  background: #fff;
  color: #3d8c40;
  border: 1px solid #3d8c40;
}

/* 图片预览弹窗 */
.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.preview-img {
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  border-radius: 8px;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .main-wrapper {
    margin: 15px auto;
    padding: 0 10px;
  }

  .toolbar {
    padding: 15px;
    flex-wrap: wrap;
    gap: 10px;
  }

  .title {
    position: static;
    transform: none;
    order: -1;
    width: 100%;
    text-align: center;
  }

  .question-list {
    padding: 0 15px;
  }

  .question-item {
    padding: 20px 0;
  }

  .modal-box {
    min-width: auto;
    width: 90%;
    padding: 30px 25px;
  }

  .modal-content {
    font-size: 16px;
  }

  .modal-btn {
    width: 110px;
    height: 40px;
    font-size: 14px;
  }
}
</style>
